import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { fetchProjectById, patchProject } from '../../../utils/api';
import RecordProfilePage from '../../../components/RecordProfilePage';
import { BIOBANK_ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../components/AuthProvider';

const InlineEditField = ({ id, value, onChange, placeholder, sx, multiline, rows, inputPropsStyle, ...props }) => (
  <TextField
    id={id}
    size="small"
    variant="standard"
    fullWidth
    multiline={multiline}
    rows={rows}
    placeholder={placeholder}
    value={value || ""}
    onChange={onChange}
    InputProps={{ disableUnderline: true }}
    {...props}
    sx={{
      bgcolor: "rgba(0, 0, 0, 0.04)",
      borderRadius: "4px",
      "& .MuiInput-root::before": { borderBottom: "none !important" },
      "& .MuiInput-root::after": { borderBottom: "none !important" },
      "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "none !important" },
      "& input": { pl: 1, pr: 1, ...inputPropsStyle },
      "& textarea": { pl: 1, pr: 1, ...inputPropsStyle },
      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.08)" },
      "& .Mui-focused": { outline: "2px solid maroon", bgcolor: "transparent", borderRadius: "4px" },
      ...sx
    }}
  />
);

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        // Fetch project by projectId
        const data = await fetchProjectById(projectId);
        setProject(data);
      } catch (err) {
        console.error(err);
        setError('Failed to load project details.');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [currentCollaborator, setCurrentCollaborator] = useState("");
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleEditClick = () => {
    if (!isAdmin) return;
    setEditData({
      projectName: project.projectName || "",
      irbNum: project.irbNum || "",
      piName: project.piName || "",
      institution: project.institution || "",
      fundingSource: project.fundingSource || "",
      objective: project.objective || "",
      description: project.description || "",
      collaborators: project.collaborators ? [...project.collaborators] : [],
    });
    setEditErrors({});
    setCurrentCollaborator("");
    setIsEditing(true);
  };

  const handleEditChange = (field) => (e) => {
    setEditData({ ...editData, [field]: e.target.value });
    setEditErrors({ ...editErrors, [field]: "" });
  };

  const handleAddCollaborator = () => {
    if (currentCollaborator.trim() !== "") {
      setEditData(prev => ({
        ...prev,
        collaborators: [...prev.collaborators, currentCollaborator.trim()]
      }));
      setCurrentCollaborator("");
    }
  };

  const handleRemoveCollaborator = (indexToRemove) => {
    setEditData(prev => ({
      ...prev,
      collaborators: prev.collaborators.filter((_, index) => index !== indexToRemove)
    }));
  };

  const validateEdit = () => {
    const newErrors = {};
    if (!editData.projectName?.trim()) newErrors.projectName = "Project Name is required";
    return newErrors;
  };

  const handleSaveEdit = async () => {
    if (!isAdmin) return;
    const validationErrors = validateEdit();
    if (Object.keys(validationErrors).length > 0) {
      setEditErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...editData
      };

      await patchProject(projectId, payload);
      
      // Update local state and close
      setProject(prev => ({
        ...prev,
        ...payload
      }));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setEditErrors({ submit: "Failed to update project." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh", bgcolor: "#fff" }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress sx={{ color: 'var(--tamu-maroon)' }} />
        </Box>
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh", bgcolor: "#fff" }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6">{error || 'Project not found.'}</Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(BIOBANK_ROUTES.PROJECT_MANAGEMENT)}
            sx={{ mt: 3, color: 'var(--tamu-maroon)' }}
          >
            Back to Projects
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <RecordProfilePage
      title="Project Details"
      backTo={BIOBANK_ROUTES.PROJECT_MANAGEMENT}
      maxWidth={1000}
      actions={
        isEditing ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setIsEditing(false)}
              variant="outlined"
              disabled={saving}
              sx={{
                textTransform: "none",
                color: "#666",
                borderColor: "#666",
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#666" }
              }}
            >
              CANCEL
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              disabled={saving}
              sx={{
                textTransform: "none",
                backgroundColor: "#500000",
                color: "white",
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: "#3a0000" }
              }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'SAVE CHANGES'}
            </Button>
          </Box>
        ) : isAdmin ? (
          <Button
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#500000",
              color: "white",
              px: 2,
              py: 1,
              "&:hover": { backgroundColor: "#3a0000" }
            }}
          >
            EDIT PROJECT
          </Button>
        ) : null
      }
    >
      <Box sx={{ pt: 1, maxWidth: "800px", mx: "auto" }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          {isEditing ? (
            <InlineEditField
              value={editData.projectName}
              onChange={handleEditChange('projectName')}
              inputPropsStyle={{ textAlign: 'center', fontSize: '2.125rem', fontWeight: 600, color: "var(--tamu-maroon)", fontFamily: "Brand Fonts, sans-serif" }}
              sx={{ mb: 1, width: '60%', mx: 'auto' }}
            />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--tamu-maroon)", mb: 1, fontFamily: "Brand Fonts, sans-serif" }}>
              {project.projectName}
            </Typography>
          )}

          <Typography sx={{ color: "#3E3E3E", fontSize: "15px", fontFamily: "Brand Fonts, sans-serif" }}>
            IRB Number: {project.irbNum || 'N/A'}
          </Typography>
          <Box sx={{ borderBottom: "1px solid #ccc", width: "100%", mx: "auto", mt: 3 }} />
        </Box>

        <Box sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Stack spacing={3}>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Principal Investigator:
              </Typography>
              {isEditing ? (
                <InlineEditField
                  value={editData.piName}
                  onChange={handleEditChange('piName')}
                  inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                />
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#3E3E3E", flex: 1, minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {project.piName || 'N/A'}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Institution:
              </Typography>
              {isEditing ? (
                <InlineEditField
                  value={editData.institution}
                  onChange={handleEditChange('institution')}
                  inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                />
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#3E3E3E", flex: 1, minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {project.institution || 'N/A'}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Funding Source:
              </Typography>
              {isEditing ? (
                <InlineEditField
                  value={editData.fundingSource}
                  onChange={handleEditChange('fundingSource')}
                  inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                />
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#3E3E3E", flex: 1, minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {project.fundingSource || 'N/A'}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Objective:
              </Typography>
              {isEditing ? (
                <InlineEditField
                  value={editData.objective}
                  onChange={handleEditChange('objective')}
                  multiline
                  rows={2}
                  inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                />
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#3E3E3E", flex: 1, whiteSpace: 'pre-wrap', minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {project.objective || 'No objective provided.'}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Collaborators:
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', minHeight: '32px' }}>
                {isEditing && (
                  <InlineEditField
                    value={currentCollaborator}
                    onChange={(e) => setCurrentCollaborator(e.target.value)}
                    placeholder="Enter to save"
                    sx={{ width: '200px' }}
                    inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCollaborator();
                      }
                    }}
                  />
                )}
                {(isEditing ? editData.collaborators : project.collaborators)?.map((c, index) => (
                  <Chip 
                    key={index} 
                    label={c} 
                    onDelete={isEditing ? () => handleRemoveCollaborator(index) : undefined}
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                ))}
                {!isEditing && (!project.collaborators || project.collaborators.length === 0) && (
                  <Typography sx={{ fontSize: "16px", color: "#3E3E3E" }}>None</Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#000", textTransform: 'uppercase', width: '280px', flexShrink: 0 }}>
                Description:
              </Typography>
              {isEditing ? (
                <InlineEditField
                  value={editData.description}
                  onChange={handleEditChange('description')}
                  multiline
                  rows={3}
                  inputPropsStyle={{ fontSize: "16px", color: "#3E3E3E" }}
                />
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#3E3E3E", flex: 1, whiteSpace: 'pre-wrap', minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {project.description || 'No description provided.'}
                </Typography>
              )}
            </Box>

          </Stack>
        </Box>
      </Box>


    </RecordProfilePage>
  );
};

export default ProjectDetails;
